import { useQuery } from "convex/react";
import { api } from "convex/_generated/api";
import type { WorkspaceId } from "@/packages/types";
import { CreditExhaustedPrompt } from "@/components/ai/CreditExhaustedPrompt";
import { SettingsSection } from "./SettingsSection";

/* ------------------------------------------------------------------ */
/* Types                                                               */
/* ------------------------------------------------------------------ */

type BalanceData = {
  workspaceId: WorkspaceId;
  monthlyAllocation: number;
  creditsUsed: number;
  creditsRemaining: number;
  periodStart: number;
  periodEnd: number;
  overageAllowed: boolean;
  usagePercent: number;
};

type OperationData = {
  operation: string;
  totalCredits: number;
  count: number;
  totalTokensInput: number;
  totalTokensOutput: number;
};

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

function getUsageColor(percent: number): string {
  if (percent > 90) return "bg-red-500";
  if (percent > 75) return "bg-orange-500";
  if (percent > 50) return "bg-yellow-500";
  return "bg-emerald-500";
}

function getUsageTextColor(percent: number): string {
  if (percent > 90) return "text-red-500";
  if (percent > 75) return "text-orange-500";
  if (percent > 50) return "text-yellow-500";
  return "text-emerald-500";
}

function formatPeriodDates(periodStart: number, periodEnd: number) {
  const start = new Date(periodStart);
  const end = new Date(periodEnd);

  const monthYear = new Intl.DateTimeFormat("en-US", {
    month: "long",
    year: "numeric",
  }).format(start);

  const resetDate = new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(end);

  return { monthYear, resetDate };
}

function formatOperationName(operation: string): string {
  return operation.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function createZeroBalance(): BalanceData {
  const now = new Date();
  const periodStart = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
  const periodEnd = new Date(
    now.getFullYear(),
    now.getMonth() + 1,
    0,
    23,
    59,
    59,
    999,
  ).getTime();

  return {
    workspaceId: "" as WorkspaceId,
    monthlyAllocation: 0,
    creditsUsed: 0,
    creditsRemaining: 0,
    periodStart,
    periodEnd,
    overageAllowed: false,
    usagePercent: 0,
  };
}

/* ------------------------------------------------------------------ */
/* CreditUsageBar                                                      */
/* ------------------------------------------------------------------ */

function CreditUsageBar({ balance }: { balance: BalanceData }) {
  const { monthYear, resetDate } = formatPeriodDates(
    balance.periodStart,
    balance.periodEnd,
  );
  const barColor = getUsageColor(balance.usagePercent);
  const textColor = getUsageTextColor(balance.usagePercent);
  const clampedPercent = Math.min(balance.usagePercent, 100);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted">
          {monthYear} &middot; Resets {resetDate}
        </p>
        <span className={`text-2xl font-bold ${textColor}`}>
          {balance.usagePercent}%
        </span>
      </div>

      <div className="space-y-2">
        <div className="h-3 w-full rounded-full bg-border/50 overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${barColor}`}
            style={{ width: `${clampedPercent}%` }}
          />
        </div>
        <div className="flex items-center justify-between text-xs text-secondary">
          <span>
            <span className="font-medium text-primary">
              {balance.creditsUsed.toLocaleString()}
            </span>{" "}
            / {balance.monthlyAllocation.toLocaleString()} credits used
          </span>
          <span>{balance.creditsRemaining.toLocaleString()} remaining</span>
        </div>
      </div>

      {balance.overageAllowed && balance.usagePercent > 90 && (
        <p className="text-xs text-orange-500">
          Overage is allowed for this workspace. Usage beyond the allocation
          will still be tracked.
        </p>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* UsageBreakdownTable                                                 */
/* ------------------------------------------------------------------ */

function UsageBreakdownTable({ data }: { data: OperationData[] }) {
  if (data.length === 0) {
    return (
      <p className="text-xs text-muted py-6 text-center">
        No AI operations recorded yet. Usage will appear here as you use AI
        features.
      </p>
    );
  }

  const totalCredits = data.reduce((sum, d) => sum + d.totalCredits, 0);

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-border">
              <th className="pb-2 text-xs font-medium text-muted">Operation</th>
              <th className="pb-2 text-xs font-medium text-muted text-right">
                Credits Used
              </th>
              <th className="pb-2 text-xs font-medium text-muted text-right">
                Requests
              </th>
              <th className="pb-2 text-xs font-medium text-muted text-right">
                Avg Credits/Request
              </th>
              <th className="pb-2 text-xs font-medium text-muted text-right">
                % of Total
              </th>
            </tr>
          </thead>
          <tbody>
            {data.map((row) => {
              const avg =
                row.count > 0
                  ? Math.round((row.totalCredits / row.count) * 10) / 10
                  : 0;
              const pctOfTotal =
                totalCredits > 0
                  ? Math.round((row.totalCredits / totalCredits) * 100)
                  : 0;

              return (
                <tr
                  key={row.operation}
                  className="border-b border-border/50 last:border-0"
                >
                  <td className="py-2.5 text-[13px] text-primary font-medium">
                    {formatOperationName(row.operation)}
                  </td>
                  <td className="py-2.5 text-[13px] text-secondary text-right tabular-nums">
                    {row.totalCredits.toLocaleString()}
                  </td>
                  <td className="py-2.5 text-[13px] text-secondary text-right tabular-nums">
                    {row.count.toLocaleString()}
                  </td>
                  <td className="py-2.5 text-[13px] text-secondary text-right tabular-nums">
                    {avg}
                  </td>
                  <td className="py-2.5 text-right">
                    <div className="inline-flex items-center gap-2">
                      <div className="w-16 h-1.5 rounded-full bg-border/50 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-accent"
                          style={{ width: `${pctOfTotal}%` }}
                        />
                      </div>
                      <span className="text-xs text-muted w-8 text-right tabular-nums">
                        {pctOfTotal}%
                      </span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Loading skeleton                                                    */
/* ------------------------------------------------------------------ */

function LoadingSkeleton() {
  return (
    <div className="space-y-4 animate-pulse">
      <div className="flex justify-between">
        <div className="space-y-2">
          <div className="h-4 w-32 bg-muted/30 rounded" />
          <div className="h-3 w-48 bg-muted/20 rounded" />
        </div>
        <div className="h-8 w-12 bg-muted/30 rounded" />
      </div>
      <div className="h-3 w-full bg-muted/20 rounded-full" />
      <div className="flex justify-between">
        <div className="h-3 w-36 bg-muted/20 rounded" />
        <div className="h-3 w-24 bg-muted/20 rounded" />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Main Component                                                      */
/* ------------------------------------------------------------------ */

export function AiUsageSection() {
  const balance = useQuery(api.ai_credit_queries.getMyBalance, {});
  const usageByOperation = useQuery(
    api.ai_credit_queries.getUsageByOperation,
    {},
  );

  // Loading state
  if (balance === undefined || usageByOperation === undefined) {
    return (
      <SettingsSection
        title="Credit Usage"
        description="Monitor your workspace's AI credit consumption."
      >
        <LoadingSkeleton />
      </SettingsSection>
    );
  }

  const resolvedBalance = balance ?? createZeroBalance();

  const isExhausted =
    resolvedBalance.monthlyAllocation > 0 &&
    resolvedBalance.creditsRemaining <= 0;

  return (
    <div className="flex flex-col gap-8">
      <SettingsSection
        title="Credit Usage"
        description="Monitor your workspace's AI credit consumption."
      >
        {isExhausted && (
          <div className="mb-4">
            <CreditExhaustedPrompt
              monthlyAllocation={resolvedBalance.monthlyAllocation}
            />
          </div>
        )}
        <CreditUsageBar balance={resolvedBalance} />
      </SettingsSection>

      <SettingsSection title="Usage by Operation">
        <UsageBreakdownTable data={usageByOperation} />
      </SettingsSection>
    </div>
  );
}
